import { Injectable, inject, signal } from '@angular/core';
import { formatDate } from '@angular/common';
import { Observable, catchError, distinctUntilChanged, map, of, switchMap, tap } from 'rxjs';
import { DashboardApiService } from '../api/dashboard-api.service';

/**
 * Holds lightweight date context derived from the backend, such as last transaction date.
 */
@Injectable({ providedIn: 'root' })
export class DateContextService {
  private readonly dashboardApi = inject(DashboardApiService);
  private _lastTransactionDateByTenant = signal<Record<string, Date | null>>({});
  private _lastTransactionLoading = signal<boolean>(false);

  /**
   * Set the last transaction date (Date in local time, date-only semantics)
   * and associate it with the provided tenant key.
   */
  setLastTransactionDateFor(tenantKey: string, date: Date | null): void {
    this._lastTransactionDateByTenant.update((current) => ({
      ...current,
      [tenantKey]: date,
    }));
  }

  /**
   * Parse an ISO date string from the API response and store the result.
   * Accepts the raw `{ last_transaction_date?: string }` shape from the backend.
   * Centralizes parsing logic so callers don't need to handle date formats.
   */
  storeLastTransactionDateFromApi(
    tenantKey: string,
    resp: { last_transaction_date?: string } | null
  ): void {
    const iso = (resp?.last_transaction_date || '').trim();
    if (!iso) {
      this.setLastTransactionDateFor(tenantKey, null);
      return;
    }
    const parsed = iso.length <= 10 ? new Date(`${iso}T00:00:00`) : new Date(iso);
    if (Number.isNaN(parsed.getTime())) {
      this.setLastTransactionDateFor(tenantKey, null);
      return;
    }
    this.setLastTransactionDateFor(
      tenantKey,
      new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate())
    );
  }

  /** Returns the last transaction date for the given tenant if known; otherwise null. */
  lastTransactionDateFor(tenantKey: string): Date | null {
    return this._lastTransactionDateByTenant()[tenantKey] ?? null;
  }

  lastTransactionDateForTenant$(tenantKey: string): Observable<Date | null> {
    if (!tenantKey) return of(null);
    const cached = this.lastTransactionDateFor(tenantKey);
    if (cached) return of(cached);
    return this.dashboardApi.getLastTransactionDate(tenantKey).pipe(
      tap((resp) => this.storeLastTransactionDateFromApi(tenantKey, resp)),
      map(() => this.lastTransactionDateFor(tenantKey)),
      catchError(() => of(null))
    );
  }

  watchFormattedLastTransactionDate(tenantKey$: Observable<string>): Observable<string> {
    return tenantKey$.pipe(
      distinctUntilChanged(),
      switchMap((tenantKey) => this.lastTransactionDateForTenant$(tenantKey)),
      map((date) => (date ? formatDate(date, 'MMM d, y', 'en-US') : ''))
    );
  }

  /** Clears any stored last transaction date data. */
  clearLastTransactionDate(): void {
    this._lastTransactionDateByTenant.set({});
  }

  /** Mark whether a fetch for last transaction date is in-flight. */
  setLastTransactionLoading(isLoading: boolean): void {
    this._lastTransactionLoading.set(isLoading);
  }

  /** True when resolver/API fetch for last transaction date is in-flight. */
  lastTransactionLoading(): boolean {
    return this._lastTransactionLoading();
  }

  /** Readonly signal for use in templates (reactive). */
  readonly lastTransactionLoadingSignal = this._lastTransactionLoading.asReadonly();
}
